import { useState, useRef, useCallback, useEffect } from "react";

type PullToRefreshOptions = {
  /** Distance in px the user must pull to trigger refresh (default: 80) */
  threshold?: number;
  /** Maximum indicator distance in px (default: 120) */
  maxPull?: number;
  /** Callback on refresh. Defaults to window.location.reload() */
  onRefresh?: () => void;
};

export function usePullToRefresh(options: PullToRefreshOptions = {}) {
  const { threshold = 80, maxPull = 120, onRefresh } = options;

  // UI state — only used for rendering
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // All gesture state in refs — avoids stale closures entirely
  const startY = useRef(0);
  const isPulling = useRef(false);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  const thresholdRef = useRef(threshold);
  const maxPullRef = useRef(maxPull);

  // Keep refs in sync with latest values
  onRefreshRef.current = onRefresh;
  thresholdRef.current = threshold;
  maxPullRef.current = maxPull;

  // Stable ref callback — identity never changes
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const setRef = useCallback((node: HTMLElement | null) => {
    setContainer(node);
  }, []);

  useEffect(() => {
    const el = container;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      // Small tolerance for sub-pixel scroll positions
      if (el.scrollTop > 1) return;
      startY.current = e.touches[0].clientY;
      isPulling.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isPulling.current || refreshingRef.current) return;

      const diff = e.touches[0].clientY - startY.current;

      if (diff <= 0) {
        pullRef.current = 0;
        setPullDistance(0);
        return;
      }

      if (el.scrollTop <= 1) {
        // Prevent native overscroll/bounce IMMEDIATELY so the browser
        // doesn't claim the gesture before we can handle it
        e.preventDefault();

        const distance = Math.min(diff * 0.5, maxPullRef.current);
        pullRef.current = distance;
        setPullDistance(distance);
      }
    };

    const onTouchEnd = () => {
      if (!isPulling.current) return;
      isPulling.current = false;

      // Read distance from ref — never stale
      const dist = pullRef.current;

      if (dist >= thresholdRef.current && !refreshingRef.current) {
        refreshingRef.current = true;
        setIsRefreshing(true);
        pullRef.current = thresholdRef.current;
        setPullDistance(thresholdRef.current);

        if (onRefreshRef.current) {
          onRefreshRef.current();
        } else {
          window.location.reload();
        }

        setTimeout(() => {
          refreshingRef.current = false;
          setIsRefreshing(false);
          pullRef.current = 0;
          setPullDistance(0);
        }, 1500);
      } else {
        pullRef.current = 0;
        setPullDistance(0);
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [container]);

  return {
    setRef,
    pullDistance,
    isRefreshing,
    isPastThreshold: pullDistance >= threshold,
  };
}
